import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Dimensions,
  Image,
  InteractionManager,
  Platform,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { check, request, PERMISSIONS, RESULTS } from "react-native-permissions";
import { getCurrentLocation as fetchDeviceLocation } from "@utils/getCurrentLocation";
import MapView, { Circle, PROVIDER_GOOGLE } from "react-native-maps";
import { useDispatch, useSelector } from "react-redux";
import SearchIcon from "@assets/svg/SearchIcon";
import Button from "@components/Button";
import CustomText from "@components/CustomText";
import { LOCATION_EVENTS } from "@constants/analyticsEventsCT";
import t from "@constants/localization";
import testIds from "@constants/testIds";
import { actions as authActions } from "@reducers/authReducer";
import { actions as miscActions } from "@reducers/miscReducer";
import analytics from "@services/analytics";
import debounce, { useDebounce } from "@utils/debounce";
import Loc from "../../assets/svg/loc";
import {
  getAddressByLatLng,
  getCLLocation,
  placesAutocomplete,
  saveDeviceLocation,
  getUserDetails,
  getLatLngByPlaceId,
} from "../../services/api";
import { scale, verticalScale } from "../../utils/scale";
import { AddressComponent } from "./AddressComponent";
import LocationItem from "./LocationItem";
import SearchModal from "./SearchModal";

const PATH = "screens/Location/index";

const Location = ({ navigation, route, ...props }) => {
  const {
    fromProfile = false,
    fromDrawer = false,
    fromSupport = false,
    fromNotServiceable = false,
    fromEnterAddress = false,
  } = route?.params ?? {};
  const { showLocationSearch = false, askLocationInside = true } = useSelector(
    (state) => state.misc,
  );

  const [lat, setLat] = useState(0);
  const [lng, setLng] = useState(0);
  const [add, setAdd] = useState("");
  const [searchData, setSearchData] = useState([]);
  // const [showSearch, setShowSearch] = useState(false);
  const [showSearch, setShowSearch] = useState(showLocationSearch);
  const [searchText, setSearchText] = useState("");
  // const [showCircle, setShowCircle] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [circleRadius, setCircleRadius] = useState(500);
  const [searching, setSearching] = useState(true);
  const dispatch = useDispatch();
  const [region, setRegion] = useState({
    latitude: null,
    longitude: null,
    latitudeDelta: 0.015,
    longitudeDelta: 0.0121,
  });
  const debouncedLocation = useDebounce(searchText, 200);
  const debounceGetLocation = React.useCallback(
    debounce(() => getLocation(), 300),
    [],
  );


  // useFocusEffect(
  //   React.useCallback(() => {
  //     const task = InteractionManager.runAfterInteractions(() => {
  //       if (!showLocationSearch || askLocationInside) {
  //         getLocation();
  //         getCLDeviceLocation();
  //       }
  //     });

  //     return () => task.cancel();
  //   }, []),
  // );

  useEffect(() => {
    const task = InteractionManager.runAfterInteractions(() => {
      if (!showLocationSearch || askLocationInside) {
        getLocation();
        getCLDeviceLocation();
      }
    });

    return () => task.cancel();
  }, []);

  useEffect(() => {
    onSearch();
  }, [onSearch, debouncedLocation]);

  useEffect(() => {
    analytics.trackEvent(LOCATION_EVENTS.LANDING);
    // getLocation();
    // getCLDeviceLocation();
  }, []);

  const getCurrentLocation = async () => {
    try {
      const position = await fetchDeviceLocation({ timeoutMs: 20000 });
      setLat(position.coords.latitude);
      setLng(position.coords.longitude);
      const mapData = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        latitudeDelta: 0.015,
        longitudeDelta: 0.0121,
      };
      setRegion(mapData);
      onRegionChangeComplete(mapData);
    } catch {
      handleGetLocationError();
    }
  };

  const getLocation = async () => {
    if (Platform.OS === "android") {
      const current = await check(PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION);
      if (current === RESULTS.GRANTED) {
        setShowSearch(false);
        getCurrentLocation();
        return;
      }
      const result = await request(PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION);
      if (result === RESULTS.GRANTED) {
        analytics.trackEvent(LOCATION_EVENTS.PERMISSION_GRANTED);
        setShowSearch(false);
        getCurrentLocation();
      } else if (result === RESULTS.BLOCKED) {
        setShowSearch(true);
        analytics.trackEvent(LOCATION_EVENTS.PERMISSION_DENIED);
        dispatch(miscActions.showSettingsModal());
      } else {
        setShowSearch(true);
        analytics.trackEvent(LOCATION_EVENTS.PERMISSION_DENIED);
      }
    }
  };

  const onRegionChangeComplete = (region) => {
    if (
      region.latitude.toFixed(5) === lat.toFixed(5) &&
      region.longitude.toFixed(5) === lng.toFixed(5) &&
      add
    ) {
      return;
    }
    if (region && typeof region === "object") {
      setRegion({
        ...region,
        latitudeDelta: Math.abs(region.latitudeDelta),
        longitudeDelta: Math.abs(region.longitudeDelta),
      });
    } else {
      setRegion(region);
    }
    setLat(region.latitude);
    setLng(region.longitude);
    addressFromLatLng(region.latitude, region.longitude);
  };

  const addressFromLatLng = debounce((lat, lng) => {
    getAddressByLatLng(lat, lng)
      .then((res) => {
        if (res.data.results.length > 0) {
          setAdd(res.data.results[0].formatted_address);
        }
      })
      .catch((err) => {
        console.log("error", err);
      });
  }, 500);

  const getLatLngFromPlaceId = ({
    place_id: placeId,
    description = "",
    structured_formatting = {},
  }) => {
    const { main_text } = structured_formatting;
    main_text ? setAdd(main_text) : setAdd(description);
    getLatLngByPlaceId(placeId)
      .then((res) => {
        const { location } = res.data.result.geometry;
        setRegion({
          latitude: location.lat,
          longitude: location.lng,
          latitudeDelta: 0.015,
          longitudeDelta: 0.0121,
        });
        setLat(location.lat);
        setLng(location.lng);
        setShowSearch(false);
      })
      .catch((err) => {
        console.log("error", err);
      });
  };

  const handleGetLocationError = () => {
    Alert.alert(
      "",
      t("location/tryAgain"),
      [
        {
          onPress: getLocation,
          text: t("location/ok"),
        },
      ],
      { cancelable: false },
    );
  };

  const saveLocation = async () => {
    setIsSubmitting(true);
    const body = {
      latitude: lat,
      longitude: lng,
    };

    if (body.latitude === null || body.longitude === null) {
      handleGetLocationError();
      return;
    }

    saveDeviceLocation(body)
      .then(async (res) => {
        analytics.trackEvent(LOCATION_EVENTS.SUBMITTED, body);
        setIsSubmitting(false);
        return getUserDetails();
      })
      .then(async (res) => {
        // update the redux saga
        const { address1, address2, lat, lng, is_serviceable, city, pincode } =
          res.data;
        if (!is_serviceable) {
          analytics.trackEvent(LOCATION_EVENTS.NOT_SERVICEABLE_SIGNUP, body);
          navigation.replace("NotServicable", {
            fromProfile,
            fromNotServiceable,
            fromDrawer,
            fromSupport,
          });
        } else if (fromEnterAddress) {
          navigation.replace("EnterAddress", {
            inputSource: "locationViaAddress",
            city,
            pincode,
            lat,
            lng,
            address1,
            address2,
          });
        } else {
          analytics.trackEvent(LOCATION_EVENTS.SERVICEABLE_SIGNUP, body);
          if (
            fromProfile === true ||
            fromNotServiceable === true ||
            fromDrawer === true ||
            fromSupport === true
          ) {
            navigation.push("TLAddress", {
              inputSource: "locationViaAddress",
              city,
              pincode,
              lat,
              lng,
              fromProfile,
              fromDrawer,
              fromSupport,
            });
          } else {
            dispatch(
              authActions.setTeamLeaderDetails({ ...res.data }),
            );
          }
        }
      })
      .catch((err) => {
        console.log("error in location", err);
        setIsSubmitting(false);
      });
  };

  const getCLDeviceLocation = () => {
    getCLLocation()
      .then(({ data }) => {
        setCircleRadius(
          data && data.serviceability_radius
            ? parseInt(data.serviceability_radius)
            : 500,
        );
      })
      .catch((err) => {});
  };

  const autoComplete = () => {
    setSearching(true);
    placesAutocomplete(searchText)
      .then((res) => {
        res?.data?.predictions && setSearchData(res.data.predictions);
        setSearching(false);
      })
      .catch((err) => {
        setSearching(false);
      });
  };
  const onSearch = useCallback(() => {
    if (debouncedLocation !== null && debouncedLocation.trim().length > 2) {
      autoComplete();
    }
  }, [debouncedLocation]);

  const onSearchIconPress = () => {
    analytics.trackEvent(LOCATION_EVENTS.SEARCHED, {});
    setShowSearch(true);
  };

  const coordsLoaded = region.latitude !== null && region.longitude !== null;

  return (
    <>
      <View testID={testIds.Map_View} style={styles.map}>
        <SearchModal
          searching={searching}
          onClose={() => setShowSearch(false)}
          value={searchText}
          onClickSearch={onSearch}
          onClearSearch={() => {
            setSearchText("");
            setSearchData([]);
          }}
          onCurrentClick={getLocation}
          data={searchData}
          renderItem={(item) => (
            <LocationItem
              onPress={() => {
                getLatLngFromPlaceId(item.item);
                setSearchText("");
                setSearchData([]);
              }}
              item={item}
            />
          )}
          showSearch={showSearch}
        />

        <Pressable
          onPress={() => onSearchIconPress()}
          style={styles.headerview}
        >
          <View style={styles.headerview2}>
            <View style={styles.headerview3}>
              <View style={styles.row}>
                <SearchIcon />
                <CustomText style={styles.searchplaceholder}>
                  {t(`${PATH}/searchLocation`)}
                </CustomText>
              </View>
            </View>
          </View>
        </Pressable>

        {coordsLoaded && (
          <MapView
            testID={testIds.Map}
            provider={PROVIDER_GOOGLE}
            style={styles.map}
            minZoomLevel={2}
            maxZoomLevel={20}
            zoomEnabled={true}
            zoomTapEnabled={true}
            pitchEnabled={true}
            gestureHandling={true}
            region={region}
            showsUserLocation={true}
            showsMyLocationButton={false}
            onRegionChangeComplete={onRegionChangeComplete}
          >
            <Circle
              zIndex={9}
              key={(lat + lng).toString()}
              center={{
                latitude: lat,
                longitude: lng,
              }}
              radius={
                typeof circleRadius === "number" && circleRadius > 0
                  ? circleRadius
                  : 500
              }
              strokeWidth={1}
              strokeColor="rgba(85,205,29,0.2)"
              fillColor="rgba(85,205,29,0.2)"
            />
          </MapView>
        )}
        <TouchableOpacity
          style={styles.loccontainer}
          onPress={debounceGetLocation}
        >
          <View style={styles.locview}>
            <Loc />
          </View>
        </TouchableOpacity>
        <View style={styles.markerFixed}>
          <Image
            resizeMode="contain"
            style={styles.marker}
            source={require("../../assets/images/you_are_here.webp")}
          />
        </View>
        <View style={styles.footer}>
          <AddressComponent
            radius={circleRadius}
            onChangeLocation={() => setShowSearch(true)}
            containerStyle={{ marginTop: verticalScale(20) }}
            textInputStyle={{ fontWeight: "600", fontSize: scale(16) }}
            textInputContainerStyle={[
              { height: add === "" ? 0 : undefined },
              styles.inputcontainer,
            ]}
            titleStyle={styles.titlestyle}
            editable={false}
            numberOfLines={1}
            value={add}
            title={t(`${PATH}/deliveryLocation`)}
          />
          <Button
            testID={testIds.Set_Map_Location}
            btnText={t(`${PATH}/useThisLocation`)}
            buttonFontType="bold"
            disabled={add === ""}
            isSubmitting={isSubmitting}
            fontSize={scale(20)}
            buttonWidth={scale(345)}
            onPress={saveLocation}
            height={scale(50)}
            borderRadius={10}
            textStyle={{ fontSize: scale(16), lineHeight: 22 }}
            containerStyle={styles.btncontainer}
          />
        </View>
      </View>
    </>
  );
};

export default Location;

const styles = StyleSheet.create({
  map: {
    flex: 1,
  },
  markerFixed: {
    left: "50%",
    marginLeft: -16,
    marginTop: -32,
    position: "absolute",
    top: "50%",
  },
  marker: {
    height: 32,
    width: 32,
  },
  footer: {
    backgroundColor: "#fff",
    borderTopEndRadius: 10,
    borderTopLeftRadius: 10,
    bottom: 0,
    position: "absolute",
    width: "100%",
  },
  region: {
    color: "#fff",
    lineHeight: 20,
    margin: 20,
  },
  headerview: {
    position: "absolute",
    zIndex: 100000,
    top: 0,
    left: 0,
    right: 0,
    flexDirection: "row",
    height: verticalScale(80),
    paddingBottom: verticalScale(10),
    overflow: "hidden",
    alignItems: "center",
    backgroundColor: "#fff",
  },
  headerview2: {
    flex: 1,
    paddingHorizontal: scale(16),
    paddingTop: verticalScale(20),
  },
  headerview3: {
    flexDirection: "row",
    paddingHorizontal: scale(10),
    alignItems: "center",
    flex: 1,
    borderWidth: 1,
    borderRadius: scale(6),
    borderColor: "rgba(0,0,0,.1)",
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
  },
  searchplaceholder: { color: "rgba(0, 0, 0, 0.36)", marginLeft: 8 },
  loccontainer: {
    position: "absolute",
    bottom: 210,
    right: 10,
  },
  locview: {
    height: scale(64),
    width: scale(64),
    backgroundColor: "white",
    borderRadius: scale(32),
    justifyContent: "center",
    alignItems: "center",
  },
  inputcontainer: {
    borderRadius: 0,
    borderWidth: 0,
    borderBottomWidth: 0,
    marginTop: 10,
    width: "100%",
  },
  titlestyle: {
    fontWeight: "600",
    fontSize: scale(12),
    flex: 1,
    padding: 0,
    color: "rgba(0,0,0,1)",
  },
  btncontainer: {
    alignSelf: "center",
    marginTop: verticalScale(20),
    borderRadius: 10,
    marginBottom: (Dimensions.get("screen").width - scale(345)) / 2,
  },
});
